// Boutiques — /shops. The one place an account connects its Shopify and
// PrestaShop shops; the orders page, the parcel monitor (notes, tracking
// numbers written on the order) and the catalogue use these connections and
// never ask for shop credentials themselves (Backend docs/shop-connections.md).

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaExclamationTriangle, FaPen, FaPlay, FaPlus, FaShopify, FaStore, FaSyncAlt, FaTrash } from 'react-icons/fa';
import ShopForm from './ShopForm';
import { CATALOG_USES, ORDER_USES, authorize, fail, formatWhen, list, remove, syncOrders, test } from './api';
import './ShopConnections.css';

const UseChips = ({ shop, t }) => {
    const missing = new Set(shop.last_test?.missing || []);
    const on = [...ORDER_USES, ...CATALOG_USES].filter((use) => shop.uses?.[use]);
    return (
        <ul className="shops-chips" aria-label={t('shops.usesTitle', 'Ce qu’Unanimity fait avec cette boutique')}>
            {on.map((use) => (
                <li key={use} className={`shops-chip ${missing.has(use) ? 'shops-chip--danger' : 'shops-chip--on'}`}
                    title={missing.has(use) ? t('shops.rightsMissing', 'Droits manquants : voir le dernier test') : undefined}>
                    {missing.has(use) ? <FaExclamationTriangle aria-hidden="true" /> : <FaCheck aria-hidden="true" />}
                    {t(`shops.uses.${use}.short`, use)}
                </li>
            ))}
        </ul>
    );
};

const ShopCard = ({ shop, canEdit, onEdit, onChanged, lang, t }) => {
    const [busy, setBusy] = useState('');
    const [result, setResult] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const sync = shop.orders_sync || {};
    const products = shop.products_sync || {};

    const act = async (kind, fn) => {
        setBusy(kind);
        setResult(null);
        try {
            await fn();
        } catch (err) {
            setResult({ ok: false, message: fail(err, t('shops.actionError', 'L’opération a échoué.')) });
        } finally {
            setBusy('');
        }
    };

    const runTest = () => act('test', async () => { const res = await test(shop.id); setResult(res.data); onChanged(); });
    const runSync = () => act('sync', async () => {
        await syncOrders(shop.id);
        setResult({ ok: true, message: t('shops.syncQueued', 'Import lancé : les nouvelles commandes arrivent dans quelques instants.') });
        onChanged();
    });
    const runDelete = () => act('delete', async () => { await remove(shop.id); onChanged(); });
    // Shopify: install the app on the shop, or approve the rights new uses need.
    const runAuthorize = () => act('authorize', async () => {
        const res = await authorize(shop.id);
        window.location.assign(res.data.install_url);
    });
    const shopify = shop.platform === 'shopify';
    const awaitingInstall = shopify && !shop.installed;

    const lastTest = result || shop.last_test;
    return (
        <article className="shops-card">
            <header className="shops-card-header">
                <span className={`shops-logo shops-logo--${shop.platform}`} aria-hidden="true">
                    {shop.platform === 'shopify' ? <FaShopify /> : <FaStore />}
                </span>
                <div className="shops-card-title">
                    <h2>{shop.name}</h2>
                    <p>
                        <span>{shop.shop_domain || shop.base_url}</span>
                        {shop.client_ref && <span className="shops-client">{t('shops.clientShort', 'Client')} : {shop.client_ref}</span>}
                    </p>
                </div>
                {awaitingInstall ? (
                    <span className="shops-status shops-status--warn">{t('shops.toInstall', 'Application à installer')}</span>
                ) : (
                    <span className={`shops-status shops-status--${shop.enabled ? 'on' : 'off'}`}>
                        {shop.enabled ? t('shops.active', 'Active') : t('shops.paused', 'En pause')}
                    </span>
                )}
            </header>

            <UseChips shop={shop} t={t} />

            <dl className="shops-facts">
                {shop.uses?.orders && (
                    <>
                        <div>
                            <dt>{t('shops.ordersImported', 'Commandes importées')}</dt>
                            <dd>{(sync.total_imported || 0).toLocaleString(lang)}</dd>
                        </div>
                        <div>
                            <dt>{t('shops.lastImport', 'Dernier import')}</dt>
                            <dd>{sync.running ? t('shops.running', 'en cours…') : formatWhen(sync.last_ok_at, lang)}</dd>
                        </div>
                    </>
                )}
                {shop.uses?.products && (
                    <div>
                        <dt>{t('shops.catalogProducts', 'Produits au catalogue')}</dt>
                        <dd>
                            {(shop.impact?.products || 0).toLocaleString(lang)}
                            {products.last_success_at ? ` · ${formatWhen(products.last_success_at, lang)}` : ''}
                            {' · '}<Link to="/catalogue/connections">{t('shops.openCatalog', 'catalogue')}</Link>
                        </dd>
                    </div>
                )}
                {lastTest && (
                    <div>
                        <dt>{t('shops.lastTest', 'Dernier test')}</dt>
                        <dd className={lastTest.ok ? 'shops-ok' : 'shops-ko'}>{lastTest.ok ? t('shops.rightsOk', 'Droits vérifiés') : t('shops.rightsKo', 'À corriger')}</dd>
                    </div>
                )}
            </dl>
            {sync.last_error && shop.uses?.orders && <p className="shops-error">{sync.last_error}</p>}
            {sync.customer_fields === false && (
                <p className="shops-note">{t('shops.customerFieldsHidden', 'Shopify ne transmet pas les coordonnées client à cette application (données client protégées) : les commandes sont importées sans contact ni adresse.')}</p>
            )}
            {lastTest && !lastTest.ok && <p className="shops-error" role="status">{lastTest.message}</p>}
            {result?.ok && <p className="shops-success" role="status">{result.message}</p>}

            {awaitingInstall && (
                <p className="shops-note">{t('shops.installPending', 'Installez l’application Unanimity sur cette boutique : Shopify vous demandera d’approuver les droits des usages cochés.')}</p>
            )}
            {shopify && shop.installed && shop.needs_authorization && (
                <p className="shops-note">{t('shops.newRights', 'Les usages cochés demandent des droits que Shopify n’a pas encore accordés : autorisez-les.')}</p>
            )}

            <div className="shops-actions">
                {canEdit && shop.needs_authorization && (
                    <button type="button" className="u-btn u-btn--primary" onClick={runAuthorize} disabled={Boolean(busy)}>
                        <FaShopify aria-hidden="true" /> {awaitingInstall ? t('shops.install', 'Installer l’application') : t('shops.reauthorize', 'Autoriser les nouveaux droits')}
                    </button>
                )}
                {canEdit && !awaitingInstall && (
                    <button type="button" className="u-btn u-btn--secondary" onClick={runTest} disabled={Boolean(busy)}>
                        <FaPlay aria-hidden="true" /> {busy === 'test' ? t('shops.testing', 'Test…') : t('shops.test', 'Tester les droits')}
                    </button>
                )}
                {shop.uses?.orders && shop.enabled && !awaitingInstall && (
                    <button type="button" className="u-btn u-btn--secondary" onClick={runSync} disabled={Boolean(busy)}>
                        <FaSyncAlt aria-hidden="true" /> {t('shops.syncNow', 'Importer les commandes')}
                    </button>
                )}
                {canEdit && (
                    <button type="button" className="shops-link" onClick={onEdit} disabled={Boolean(busy)}>
                        <FaPen aria-hidden="true" /> {t('shops.edit', 'Modifier')}
                    </button>
                )}
                {canEdit && (confirming ? (
                    <span className="shops-confirm">
                        {shop.impact?.products
                            ? t('shops.confirmDeleteProducts', 'Déconnecter ? Les {{n}} produits du catalogue de cette boutique seront supprimés ; les commandes importées restent.', { n: shop.impact.products })
                            : t('shops.confirmDelete', 'Déconnecter ? Les commandes importées restent.')}
                        <button type="button" className="shops-link shops-link--danger" onClick={runDelete} disabled={Boolean(busy)}>{t('shops.confirm', 'Confirmer')}</button>
                        <button type="button" className="shops-link" onClick={() => setConfirming(false)}>{t('shops.cancel', 'Annuler')}</button>
                    </span>
                ) : (
                    <button type="button" className="shops-link shops-link--danger" onClick={() => setConfirming(true)} disabled={Boolean(busy)}>
                        <FaTrash aria-hidden="true" /> {t('shops.disconnect', 'Déconnecter')}
                    </button>
                ))}
            </div>
        </article>
    );
};

const ShopConnections = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(null);       // null | 'new' | shop
    const [notice, setNotice] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await list();
            setData(res.data);
        } catch (err) {
            setError(fail(err, t('shops.loadError', 'Impossible de charger les boutiques.')));
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);
    // Back from Shopify's install page.
    useEffect(() => {
        const outcome = searchParams.get('shopify');
        if (!outcome) return;
        const shopId = searchParams.get('shop');
        const reasons = {
            signature: t('shops.installErrorSignature', 'La réponse de Shopify n’a pas pu être vérifiée. Recommencez l’installation.'),
            expired: t('shops.installErrorExpired', 'Le lien d’installation a expiré (30 minutes) ou a déjà servi. Recommencez l’installation.'),
            shop_mismatch: t('shops.installErrorShop', 'L’application a été installée sur une autre boutique que celle indiquée. Recommencez depuis la bonne boutique.'),
            denied: t('shops.installErrorDenied', 'L’installation n’a pas été approuvée sur Shopify.'),
            exchange: t('shops.installErrorExchange', 'Shopify n’a pas délivré l’autorisation. Recommencez dans quelques instants.'),
            shop: t('shops.installErrorShop', 'L’application a été installée sur une autre boutique que celle indiquée. Recommencez depuis la bonne boutique.'),
        };
        if (outcome === 'installed' && shopId) {
            (async () => {
                try {
                    const res = await test(shopId);
                    setNotice({ ok: res.data.ok, message: res.data.ok
                        ? t('shops.installed', 'Application installée. {{message}}', { message: res.data.message })
                        : res.data.message });
                } catch (e) {
                    setNotice({ ok: true, message: t('shops.installedShort', 'Application installée sur la boutique.') });
                }
                load();
            })();
        } else {
            setNotice({ ok: false, message: reasons[searchParams.get('reason')] || t('shops.installError', 'L’installation n’a pas abouti.') });
        }
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            ['shopify', 'shop', 'reason'].forEach((k) => p.delete(k));
            return p;
        }, { replace: true });
    }, [searchParams, setSearchParams, t, load]);

    useEffect(() => {
        if (searchParams.get('add') && data?.can_edit) {
            setEditing('new');
            setSearchParams((prev) => { const p = new URLSearchParams(prev); p.delete('add'); return p; }, { replace: true });
        }
    }, [searchParams, data, setSearchParams]);

    const saved = async (shop, { created }) => {
        setEditing(null);
        if (created) {
            // A new connection is tested right away: rights are what fail first.
            try {
                const res = await test(shop.id);
                setNotice(res.data.ok ? { ok: true, message: res.data.message } : null);
            } catch (e) { /* the card shows the stored verdict */ }
        }
        load();
    };

    if (error) return <div className="shops-page"><p className="shops-error" role="alert">{error}</p></div>;
    if (!data) return <div className="shops-page"><p className="shops-note">{t('shops.loading', 'Chargement…')}</p></div>;
    const shops = data.connections || [];

    return (
        <div className="shops-page">
            <header className="shops-header">
                <div>
                    <h1>{t('shops.title', 'Boutiques')}</h1>
                    <p>{t('shops.subtitle', 'Connectez une fois chaque boutique Shopify ou PrestaShop : les commandes, la surveillance des colis et le catalogue s’en servent.')}</p>
                </div>
                {data.can_edit && !editing && (
                    <button type="button" className="u-btn u-btn--primary" onClick={() => setEditing('new')}>
                        <FaPlus aria-hidden="true" /> {t('shops.add', 'Connecter une boutique')}
                    </button>
                )}
            </header>

            {!data.can_edit && <p className="shops-note">{t('shops.adminOnly', 'Seul un administrateur du compte peut connecter ou modifier une boutique.')}</p>}
            {notice && <p className={notice.ok ? 'shops-success' : 'shops-error'} role="status">{notice.message}</p>}
            {!data.parcel_monitoring && shops.some((s) => s.uses?.orders) && (
                <p className="shops-note">
                    {t('shops.monitoringOff', 'Les commandes sont importées, mais leurs colis ne sont surveillés que si la surveillance colis est active.')}
                    {' '}<Link to="/parcel-monitor">{t('shops.openMonitoring', 'Surveillance colis')}</Link>
                </p>
            )}

            {editing && (
                <ShopForm shop={editing === 'new' ? null : editing} meta={data} onSaved={saved} onCancel={() => setEditing(null)} />
            )}

            {shops.length === 0 && !editing ? (
                <div className="shops-empty">
                    <h2>{t('shops.emptyTitle', 'Aucune boutique connectée')}</h2>
                    <p>{t('shops.emptyText', 'Shopify (application Unanimity ou jeton) et PrestaShop (clé Webservice) : aucun module à installer.')}</p>
                </div>
            ) : (
                <div className="shops-list">
                    {shops.map((shop) => (
                        <ShopCard key={shop.id} shop={shop} canEdit={data.can_edit} lang={lang} t={t}
                            onEdit={() => setEditing(shop)} onChanged={load} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ShopConnections;
